'use client';

import Link from 'next/link';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Eye, Clock } from 'lucide-react';

interface HelpArticle {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  views: number;
  isFeatured: boolean;
  createdAt: Date;
  category?: {
    name: string;
    slug: string;
  };
}

interface HelpArticleCardProps {
  article: HelpArticle;
  showCategory?: boolean;
}

export function HelpArticleCard({ article, showCategory = false }: HelpArticleCardProps) {
  const href = article.category
    ? `/help/${article.category.slug}/${article.slug}`
    : `/help/${article.slug}`;

  return (
    <Link
      href={href}
      className="block transition-transform hover:scale-[1.02]"
    >
      <Card className="h-full cursor-pointer hover:border-primary hover:shadow-md">
        <CardHeader>
          <div className="mb-2 flex items-center gap-2">
            {article.isFeatured && (
              <Badge variant="secondary" className="text-xs">
                Featured
              </Badge>
            )}
            {showCategory && article.category && (
              <Badge variant="outline" className="text-xs">
                {article.category.name}
              </Badge>
            )}
          </div>
          <CardTitle className="line-clamp-2 text-lg">{article.title}</CardTitle>
          {article.excerpt && (
            <CardDescription className="line-clamp-3">{article.excerpt}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              <Eye className="h-4 w-4" />
              <span>{article.views.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>{new Date(article.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
